import React from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

import { TaskService } from 'components/services/TaskService';
import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

interface AutoAssignmentDialogProps {
  visible: boolean;
  response: any;
  onHide: () => void;
  taskId?: number;
  onDistrictChange: (e: any) => void;
  isDepartmentDeleted?: boolean;
  isDirty: boolean;
}

const errorMessages: Record<string, string> = {
  taskStartError: 'Ошибка начала наряда',
  taskFinishError: 'Ошибка завершения наряда',
  assetError: 'Ошибка объекта',
  assetAddressError: 'Ошибка адреса объекта',
  taskNotFound: 'Наряд не найден',
  taskTypeError: 'Ошибка типа наряда',
  taskTypeResolutionTimeError: 'Ошибка планового завершения типа наряда',
  taskTypeResponseTimeError: 'Ошибка времени планового начала типа наряда',
  taskTypeProcessingTimeError: 'Ошибка времени обработки типа наряда',
  departmentNotFound: 'Участок не найден',
  planningAlgorithmError: 'В типе наряда не задан алгоритм распределения'
};

const isAssignmentSuccessful = (response: any): boolean => {
  return response?.isSuccess === true && response?.value?.[0]?.department;
};

const getAssignmentErrors = (response: any): string[] => {
  if (typeof response === 'string') return [response];
  if (!response?.value?.[0]?.errors) return [];
  return response.value[0].errors;
};

export const AutoAssignmentDialog: React.FC<AutoAssignmentDialogProps> = ({
  visible,
  response,
  onHide,
  taskId,
  onDistrictChange,
  isDepartmentDeleted = false,
  isDirty
}) => {
  if (isDepartmentDeleted && isDirty) {
    return (
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Невозможно выполнить автораспределение"
        style={{ minWidth: '500px', width: '500px' }}
        pt={withDialogFooterSpacing()}
        footer={
          <div className="flex gap-3 justify-content-end">
            <Button
              label="Закрыть"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={onHide}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3 p-2">
          <div className="flex align-items-center gap-2">
            <i
              className="pi pi-exclamation-triangle"
              style={{ fontSize: '1.5rem', color: '#f59e0b' }}
            />
            <span className="font-medium">Действие недоступно</span>
          </div>

          <div
            className="flex flex-column p-3 border-1 border-round"
            style={{
              backgroundColor: '#fffbeb',
              borderColor: '#fde68a',
              borderRadius: '8px',
              gap: '8px'
            }}
          >
            <div className="flex align-items-center gap-2">
              <i
                className="pi pi-info-circle"
                style={{ fontSize: '1rem', color: '#d97706' }}
              />
              <span style={{ color: '#92400e', fontWeight: 500 }}>
                Для того чтобы автораспределение было доступно, необходимо
                сохранить изменения наряда
              </span>
            </div>
          </div>
        </div>
      </Dialog>
    );
  }

  if (!response) return null;

  const isSuccess = isAssignmentSuccessful(response);
  const department = response?.value?.[0]?.department;
  const errors = getAssignmentErrors(response);

  const handleConfirm = async () => {
    if (taskId) {
      try {
        await TaskService.AutoTaskAssignment([taskId], false);

        if (onDistrictChange && department) {
          onDistrictChange({
            key: department.id.toString(),
            label: department.label
          });
        }

        onHide();
      } catch (error) {
        console.error('Error confirming auto assignment:', error);
      }
    } else {
      onHide();
    }
  };

  if (isSuccess && department) {
    return (
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Участок подобран"
        style={{ minWidth: '500px', width: '500px' }}
        pt={withDialogFooterSpacing()}
        footer={
          <div className="flex gap-3 justify-content-end">
            <Button
              label="Отмена"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={onHide}
            />
            <Button
              label="Подтвердить назначение"
              icon="pi pi-check"
              onClick={() => {
                handleConfirm();
                onHide();
              }}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3 p-2">
          <div className="flex align-items-center gap-2">
            <i
              className="pi pi-check-circle"
              style={{ fontSize: '1.5rem', color: '#22c55e' }}
            />
            <span className="font-medium">Подобран следующий участок:</span>
          </div>

          <div
            className="flex align-items-center p-3 border-1 border-round"
            style={{
              backgroundColor: '#f0fdf4',
              borderColor: '#bbf7d0',
              borderRadius: '8px'
            }}
          >
            <i
              className="pi pi-building mr-3"
              style={{ fontSize: '1.25rem', color: '#16a34a' }}
            />
            <span
              className="text-lg font-semibold"
              style={{ color: '#166534' }}
            >
              {department.label}
            </span>
          </div>

          <div className="text-sm text-gray-500 mt-1">
            После подтверждения участок будет назначен наряд
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Ошибка подбора участка"
      style={{ minWidth: '500px', width: '500px' }}
      pt={withDialogFooterSpacing()}
      footer={
        <div className="flex gap-3 justify-content-end">
          <Button
            label="Закрыть"
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={onHide}
          />
        </div>
      }
    >
      <div className="flex flex-column gap-3 p-2">
        <div className="flex align-items-center gap-2">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: '1.5rem', color: '#ef4444' }}
          />
          <span className="font-medium">Не удалось подобрать участок</span>
        </div>

        <div
          className="flex flex-column p-3 border-1 border-round"
          style={{
            backgroundColor: '#fef2f2',
            borderColor: '#fecaca',
            borderRadius: '8px',
            gap: '8px'
          }}
        >
          {errors.length > 0 ? (
            errors.map((error: string, index: number) => (
              <div key={index} className="flex align-items-center gap-2">
                <i
                  className="pi pi-times-circle"
                  style={{ fontSize: '0.875rem', color: '#dc2626' }}
                />
                <span style={{ color: '#991b1b' }}>
                  {errorMessages[error] || error}
                </span>
              </div>
            ))
          ) : (
            <div className="flex align-items-center gap-2">
              <i
                className="pi pi-times-circle"
                style={{ fontSize: '0.875rem', color: '#dc2626' }}
              />
              <span style={{ color: '#991b1b' }}>
                Неизвестная ошибка при подборе участка
              </span>
            </div>
          )}
        </div>

        <div className="text-sm text-gray-500 mt-1">
          Пожалуйста, выберите участок вручную или проверьте настройки
        </div>
      </div>
    </Dialog>
  );
};
